"use client";

import {
	FlaskConical,
	Truck,
	HelpCircle,
	MessageSquare,
	ArrowRight,
	Beaker,
	Search,
	Droplets,
	Scale,
	Sprout,
	HeartPulse,
	Sun,
	Hourglass,
	Brain,
	BookOpen,
	LayoutGrid,
	AlertTriangle,
	ClipboardList,
	ClipboardPen,
} from "lucide-react";

type IconComponent = React.ComponentType<{ className?: string }>;

const CATEGORY_ICONS: Record<string, IconComponent> = {
	"weight-management": Scale,
	"growth-hormone": Sprout,
	recovery: HeartPulse,
	skin: Sun,
	"anti-aging": Hourglass,
	cognition: Brain,
	"bac-water": Droplets,
};

export function getCategoryIcon(slug: string): IconComponent {
	return CATEGORY_ICONS[slug] ?? FlaskConical;
}
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import { cn } from "@/lib/utils";
import Image from "next/image";

interface CategoryItem {
	id: string;
	name: string;
	slug: string;
	description?: string | null;
}

interface FeaturedProduct {
	name: string;
	slug: string;
	imageUrl: string | null;
	pricePresentation: ResolvedMoneyPresentation | null;
	locale: string;
	channel: string;
}

function FeaturedProductPriceLine({
	presentation,
	locale,
	channel,
}: {
	presentation: ResolvedMoneyPresentation | null;
	locale: string;
	channel: string;
}) {
	if (!presentation) return null;
	return (
		<p className="mt-1 tabular-nums">
			<ReferencePrimaryPrice
				presentation={presentation}
				locale={locale}
				channel={channel}
				size="sm"
				className="text-sm font-bold text-[var(--primary-hero)]"
			/>
		</p>
	);
}

interface PeptidesPanelLabels {
	categories: string;
	featured: string;
	browseAll: string;
	allPeptides: string;
	viewProduct: string;
}

interface ResearchPanelLabels {
	guide: string;
	guideDesc: string;
	documentation: string;
	partners: string;
	partnersDesc: string;
	aboutUs: string;
	aboutUsDesc: string;
	journal: string;
	journalDesc: string;
	science: string;
	scienceDesc: string;
	applications: string;
	applicationsDesc: string;
	ruo: string;
	ruoDesc: string;
	dossier: string;
	dossierDesc: string;
	inquiry: string;
	inquiryDesc: string;
}

interface SupportPanelLabels {
	shippingOrders: string;
	shipping: string;
	help: string;
	faq: string;
	contact: string;
}

function ColumnHeading({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="after:bg-primary/40 text-foreground/70 relative mb-4 pb-2 text-xs font-bold uppercase tracking-widest after:absolute after:bottom-0 after:left-0 after:h-px after:w-10">
			{children}
		</h3>
	);
}

function MenuLink({
	href,
	icon: Icon,
	label,
	description,
	className,
}: {
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	description?: string;
	className?: string;
}) {
	return (
		<LinkWithChannel
			href={href}
			className={cn(
				"group -ms-5 flex items-start gap-3 rounded-xl p-2.5 transition-all duration-150 hover:bg-accent hover:text-accent-foreground",
				className,
			)}
		>
			<div className="bg-primary/10 group-hover:bg-primary/20 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors">
				<Icon className="h-4 w-4 text-primary" />
			</div>
			<div className="group-hover:ps-1.5 min-w-0 pt-0.5 transition-all">
				<span className="text-[13px] font-medium text-foreground">{label}</span>
				{description && (
					<p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-muted-foreground">{description}</p>
				)}
			</div>
		</LinkWithChannel>
	);
}

export function PeptidesPanel({
	categories,
	featured,
	labels,
}: {
	categories: CategoryItem[];
	featured: FeaturedProduct | null;
	labels: PeptidesPanelLabels;
}) {
	return (
		<div className="grid grid-cols-[1fr_280px] gap-0">
			<div className="pr-10">
				<ColumnHeading>{labels.categories}</ColumnHeading>
				<LinkWithChannel
					href="/peptides"
					className="hover:bg-primary/5 group -ms-2 mb-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-primary transition-colors"
				>
					{labels.browseAll}
					<ArrowRight className="group-hover:translate-x-0.5 h-3.5 w-3.5 transition-transform" />
				</LinkWithChannel>
				<div className="grid grid-cols-2 gap-x-1">
					{categories.map((cat) => {
						const Icon = getCategoryIcon(cat.slug);
						return (
							<LinkWithChannel
								key={cat.id}
								href={`/categories/${cat.slug}`}
								className="group -ms-2 flex min-h-[40px] items-center gap-2.5 rounded-lg px-2 py-1.5 transition-all duration-150 hover:bg-accent"
							>
								<span className="bg-primary/10 group-hover:bg-primary/20 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors">
									<Icon className="h-3.5 w-3.5 text-primary" />
								</span>
								<span className="group-hover:ps-0.5 min-w-0 text-[13px] font-medium text-foreground transition-all">
									{cat.name}
								</span>
							</LinkWithChannel>
						);
					})}
					<LinkWithChannel
						href="/products/bac-water-10ml"
						className="group -ms-2 flex min-h-[40px] items-center gap-2.5 rounded-lg px-2 py-1.5 transition-all duration-150 hover:bg-accent"
					>
						<span className="bg-primary/10 group-hover:bg-primary/20 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors">
							<Droplets className="h-3.5 w-3.5 text-primary" />
						</span>
						<span className="group-hover:ps-0.5 min-w-0 text-[13px] font-medium text-foreground transition-all">
							BAC Water
						</span>
					</LinkWithChannel>
				</div>
			</div>

			<div className="border-border/40 border-l pl-8">
				<ColumnHeading>{labels.featured}</ColumnHeading>
				{featured ? (
					<LinkWithChannel
						href={`/products/${featured.slug}`}
						className="hover:border-primary/40 group block overflow-hidden rounded-xl border border-border bg-card transition-all duration-200 hover:shadow-lg"
					>
						{featured.imageUrl && (
							<div className="relative aspect-[3/2] bg-secondary">
								<Image
									src={featured.imageUrl}
									alt={featured.name}
									fill
									className="group-hover:scale-105 object-cover transition-transform duration-500 ease-out"
								/>
							</div>
						)}
						<div className="p-3.5">
							<p className="line-clamp-2 text-[13px] font-semibold text-foreground">{featured.name}</p>
							<FeaturedProductPriceLine
								presentation={featured.pricePresentation}
								locale={featured.locale}
								channel={featured.channel}
							/>
							<span className="bg-primary/10 group-hover:bg-primary/20 mt-2.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-primary transition-colors">
								{labels.viewProduct}
								<ArrowRight className="group-hover:translate-x-0.5 h-3 w-3 transition-transform" />
							</span>
						</div>
					</LinkWithChannel>
				) : (
					<div className="rounded-xl border border-dashed border-border p-6 text-center">
						<Beaker className="text-muted-foreground/40 mx-auto h-8 w-8" />
						<p className="mt-2 text-xs text-muted-foreground">{labels.browseAll}</p>
					</div>
				)}
			</div>
		</div>
	);
}

export function ResearchPanel({ labels }: { labels: ResearchPanelLabels }) {
	return (
		<div>
			<ColumnHeading>{labels.documentation}</ColumnHeading>
			<div className="grid max-w-2xl grid-cols-2 gap-x-4 gap-y-2">
				<MenuLink href="/journal" icon={BookOpen} label={labels.journal} description={labels.journalDesc} />
				<MenuLink
					href="/science"
					icon={FlaskConical}
					label={labels.science}
					description={labels.scienceDesc}
				/>
				<MenuLink
					href="/applications"
					icon={LayoutGrid}
					label={labels.applications}
					description={labels.applicationsDesc}
				/>
				<MenuLink
					href="/research-use-only"
					icon={AlertTriangle}
					label={labels.ruo}
					description={labels.ruoDesc}
				/>
				<MenuLink
					href="/dossier"
					icon={ClipboardList}
					label={labels.dossier}
					description={labels.dossierDesc}
				/>
				<MenuLink
					href="/anfrage"
					icon={ClipboardPen}
					label={labels.inquiry}
					description={labels.inquiryDesc}
				/>
				<MenuLink href="/peptides" icon={Search} label={labels.guide} description={labels.guideDesc} />
			</div>
		</div>
	);
}

export function SupportPanel({ labels }: { labels: SupportPanelLabels }) {
	const cards = [
		{ href: "/shipping", icon: Truck, title: labels.shipping, sub: labels.shippingOrders },
		{ href: "/faq", icon: HelpCircle, title: labels.faq, sub: labels.help },
		{ href: "/contact", icon: MessageSquare, title: labels.contact, sub: null },
	] as const;

	return (
		<div className="grid grid-cols-3 gap-4">
			{cards.map(({ href, icon: Icon, title, sub }) => (
				<LinkWithChannel
					key={href}
					href={href}
					className="hover:border-primary/30 group flex flex-col gap-3 rounded-xl border border-border bg-background p-5 transition-all duration-150 hover:shadow-md"
				>
					<div
						className="group-hover:bg-primary/20 flex h-10 w-10 items-center justify-center rounded-full transition-colors"
						style={{ background: "color-mix(in oklch, var(--primary) 10%, transparent)" }}
					>
						<Icon className="h-5 w-5 text-primary" />
					</div>
					<div className="flex-1">
						<p className="text-sm font-semibold text-foreground">{title}</p>
						{sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
					</div>
					<span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
						{title}
						<ArrowRight className="group-hover:translate-x-0.5 h-3 w-3 transition-transform" />
					</span>
				</LinkWithChannel>
			))}
		</div>
	);
}

export type { CategoryItem, FeaturedProduct, PeptidesPanelLabels, ResearchPanelLabels, SupportPanelLabels };
